import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export type TeachingDashboard = {
  academicSession: string
  today: string
  pendingLogs: number
  verifiedLogs: number
  openDeviations: number
  scheduledPeriodsToday: number
  missingTeachingLogs: number
}

export type TodaySlot = {
  date: string
  dayName: string
  periodKey: string
  className: string
  section: string
  subjectName: string
  classRowId: string
  label: string
  scheduledTeacherName?: string
  teachingLog: { _id: string; logStatus: string; periodOutcome: string } | null
  lessonPlans: Array<{ _id: string; title: string; status: string }>
  prepared: boolean
}

export type TopicCoverageRow = {
  topicId: string
  title: string
  plannedPeriods: number
  actualPeriods: number
  periodVariance: number
  completionStatus: string
  firstTeachingDate: string
  lastTeachingDate: string
  evidenceLogCount: number
  missingEvidence: boolean
}

const base = '/acdmc/teaching'

export const fetchTeachingDashboard = () =>
  api.get<{ success: boolean; data: TeachingDashboard }>(`${base}/dashboard`).then((r) => r.data.data)

export const fetchTodaySchedule = (date?: string) =>
  api.get<{ success: boolean; data: { slots: TodaySlot[] } }>(`${base}/today`, { params: date ? { date } : {} }).then((r) => r.data.data)

export const createTeachingLog = (body: Record<string, unknown>) =>
  api.post(`${base}/logs`, body).then((r) => r.data.data)

export const updateTeachingLog = (id: string, body: Record<string, unknown>) =>
  api.patch(`${base}/logs/${id}`, body).then((r) => r.data.data)

export const fetchCoverage = (params: { className: string; section?: string; subjectName: string }) =>
  api
    .get<{ success: boolean; data: { rows: TopicCoverageRow[]; summary: Record<string, unknown> } }>(`${base}/coverage`, { params })
    .then((r) => r.data.data)

export function useTeachingDashboard() {
  return useQuery({ queryKey: ['acdmc', 'teaching', 'dashboard'], queryFn: fetchTeachingDashboard })
}

export function useTodaySchedule(date?: string) {
  return useQuery({ queryKey: ['acdmc', 'teaching', 'today', date], queryFn: () => fetchTodaySchedule(date) })
}

export function useTopicCoverage(params: { className: string; section: string; subjectName: string } | null) {
  return useQuery({
    queryKey: ['acdmc', 'teaching', 'coverage', params],
    queryFn: () => fetchCoverage(params!),
    enabled: Boolean(params?.className && params?.subjectName),
  })
}

export function useSaveTeachingLog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id ? updateTeachingLog(id, body) : createTeachingLog(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'teaching'] })
    },
  })
}
